/**
 * Centralized Timezone Utilities
 *
 * This module provides all timezone-related functionality for the application.
 * All timezone conversion, detection, and formatting logic should be added here
 * to avoid duplication across components.
 *
 * Key functions:
 * - getNowInTimezone(): Get current time in a specific timezone
 * - getTimezoneAbbreviation(): Get short timezone name (EST, PST, etc.)
 * - detectBrowserTimezone(): Detect user's browser timezone
 * - areTimezonesEquivalent(): Compare timezone aliases
 *
 * Note: For timezone dropdown options, use useGetAvailableTimezonesQuery() from
 * redux/apiSlices/organization.slice.ts instead of hardcoded values.
 *
 * @module helpers/timezoneUtils
 */
import moment, { Moment } from 'moment';

export type TTimezoneOption = {
  label: string;
  value: string;
};

/**
 * Detect the browser's current timezone
 * Returns IANA timezone identifier (e.g., "America/New_York")
 */
export const detectBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

/**
 * Check if two timezone identifiers are equivalent
 * Handles aliases like "US/Eastern" vs "America/New_York"
 */
export const areTimezonesEquivalent = (tz1: string, tz2: string): boolean => {
  if (!tz1 || !tz2) return false;
  if (tz1 === tz2) return true;

  try {
    // Compare by getting the current offset for both timezones
    const now = new Date();
    const formatter1 = new Intl.DateTimeFormat('en-US', {
      timeZone: tz1,
      timeZoneName: 'short',
    });
    const formatter2 = new Intl.DateTimeFormat('en-US', {
      timeZone: tz2,
      timeZoneName: 'short',
    });

    return formatter1.format(now) === formatter2.format(now);
  } catch {
    return false;
  }
};

/**
 * Get a human-readable timezone display name
 */
export const getTimezoneDisplayName = (timezone: string): string => {
  if (!timezone) return '';

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'long',
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    return tzPart?.value || timezone;
  } catch {
    return timezone;
  }
};

/**
 * Mapping for timezones where browsers return "GMT+X" instead of proper abbreviations.
 *
 * Fallback behavior for unmapped timezones:
 * 1. Uses browser's Intl.DateTimeFormat to get the abbreviation
 * 2. If that fails, returns the timezone identifier (e.g., "Asia/Bangkok")
 */
const TIMEZONE_ABBREVIATIONS: Record<string, string> = {
  'Asia/Manila': 'PHT',
  'Asia/Singapore': 'SGT',
  'Asia/Hong_Kong': 'HKT',
  'Asia/Tokyo': 'JST',
  'Asia/Seoul': 'KST',
  'Asia/Shanghai': 'CST',
  'Asia/Kolkata': 'IST',
  'Asia/Dubai': 'GST',
  'Asia/Yerevan': 'AMT',
  'Europe/Moscow': 'MSK',
  'Australia/Sydney': 'AEST',
  'Australia/Melbourne': 'AEST',
  'Australia/Brisbane': 'AEST',
  'Australia/Perth': 'AWST',
  'Pacific/Auckland': 'NZST',
};

/**
 * Get the short timezone abbreviation (e.g., "EST", "PST", "UTC")
 */
export const getTimezoneAbbreviation = (timezone: string): string => {
  if (!timezone) return '';

  // Check if we have a known abbreviation for this timezone
  if (TIMEZONE_ABBREVIATIONS[timezone]) {
    return TIMEZONE_ABBREVIATIONS[timezone];
  }

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    return tzPart?.value || timezone;
  } catch {
    return timezone;
  }
};

/**
 * ZEST-581: Get the current time converted to a specific timezone
 * @param timeZone - IANA timezone identifier (e.g., "America/New_York", "US/Mountain")
 * @returns Moment object representing current time in the specified timezone
 */
export const getNowInTimezone = (timeZone: string): Moment => {
  try {
    const now = new Date();
    // Using 'en-US' locale for consistent date parsing (locale affects format only, not timezone conversion)
    const invdate = new Date(now.toLocaleString('en-US', { timeZone }));
    const diff = now.getTime() - invdate.getTime();
    return moment(new Date(now.getTime() - diff));
  } catch (error) {
    console.warn(`Invalid timezone: ${timeZone}, falling back to local time`);
    return moment();
  }
};
